import { registerComponent, createMessage, loading } from "helpers";
import WYATTElement from "wyatt";

const DISCOUNT_FAILURE_REASONS = new Set([
  "invalid",
  "expired",
  "min_spend",
  "already_redeemed",
]);

const REASON_MESSAGE_KEYS = {
  invalid: "invalid",
  expired: "expired",
  min_spend: "minSpend",
  already_redeemed: "alreadyRedeemed",
};

const DEFAULT_INVALID_MESSAGE =
  "Hmm, that promo code didn’t work. Please double-check and try again.";

const DISCOUNT_VALIDATION_CODE_ATTRIBUTE = "discount_validation_code";

function discountCodesToPayload(codes) {
  const seen = new Set();

  const unique = codes
    .map((raw) => raw.trim())
    .filter(Boolean)
    .filter((code) => {
      const key = code.toLowerCase();

      if (seen.has(key)) {
        return false;
      }

      seen.add(key);
      return true;
    });

  return unique.join(",");
}

function requestedDiscountCodes(discountParam) {
  if (!discountParam?.trim()) {
    return [];
  }

  return discountParam
    .split(",")
    .map((c) => c.trim())
    .filter(Boolean);
}

function getDiscountCodeRowsFromResponse(parsedState) {
  if (!parsedState || typeof parsedState !== "object") {
    return null;
  }

  if (Array.isArray(parsedState.discount_codes)) {
    return parsedState.discount_codes;
  }

  return null;
}

function getInapplicableRequestedCodes(
  discountParam,
  discountCodesFromResponse,
) {
  const requested = requestedDiscountCodes(discountParam);

  if (requested.length === 0 || discountCodesFromResponse === null) {
    return [];
  }

  const rows = discountCodesFromResponse;

  return requested.flatMap((req) => {
    const key = req.toLowerCase();
    const row = rows.find((d) => d.code?.trim().toLowerCase() === key);

    if (!row || row.applicable === false) {
      return [row?.code?.trim() || req];
    }

    return [];
  });
}

function resolveCartValidationBaseUrl() {
  const cartUrl = WYATT.routes.cart_url || "/cart";

  if (cartUrl.startsWith("http")) {
    return new URL(cartUrl);
  }

  const root = WYATT.routes.root || "/";
  const path = cartUrl.startsWith("/")
    ? cartUrl
    : `${root}${cartUrl}`.replace(/([^:]\/)\/+/g, "$1");

  return new URL(path, window.location.origin);
}

function buildDiscountValidationUrl() {
  const url = resolveCartValidationBaseUrl();

  url.searchParams.set("view", "discount-validation");

  return `${url.pathname}${url.search}`;
}

function getFailedDiscountCode(inapplicable, attemptedCode) {
  const trimmedAttempt = attemptedCode?.trim();

  if (trimmedAttempt) {
    const attemptedLower = trimmedAttempt.toLowerCase();
    const match = inapplicable.find(
      (failedCode) => failedCode.toLowerCase() === attemptedLower,
    );

    return match ?? trimmedAttempt;
  }

  return inapplicable[0];
}

function getDiscountErrorMessage(reason) {
  const messageKey = REASON_MESSAGE_KEYS[reason] ?? REASON_MESSAGE_KEYS.invalid;
  const messages = WYATT.t?.cartDiscountCode ?? {};

  return messages[messageKey] ?? messages.invalid ?? DEFAULT_INVALID_MESSAGE;
}

function parseDiscountValidationResponse(responseText) {
  if (!responseText?.trim()) {
    return "invalid";
  }

  let data;

  try {
    data = JSON.parse(responseText.trim());
  } catch {
    return "invalid";
  }

  const reason = data?.reason;

  if (DISCOUNT_FAILURE_REASONS.has(reason)) {
    return reason;
  }

  return "invalid";
}

async function resolveDiscountFailureReason() {
  try {
    // Request the Liquid template alternate as HTML. Accept: application/json
    // makes Shopify return /cart.js instead of the validation template output.
    // The promo code is read from cart.attributes via the preceding cart update.
    const response = await fetch(buildDiscountValidationUrl());

    if (!response.ok) {
      return "invalid";
    }

    return parseDiscountValidationResponse(await response.text());
  } catch {
    return "invalid";
  }
}

function clearCartDiscountMessageHosts() {
  [...document.querySelectorAll("[data-cart-discount-messages]")].forEach(
    (el) => {
      el.replaceChildren();
    },
  );
}

function cartPayloadFromUpdateResponse(parsedState) {
  const cart = { ...parsedState };

  delete cart.sections;
  delete cart.sections_url;

  return cart;
}

registerComponent(
  "cart-discount-code",
  class CartDiscountCode extends WYATTElement {
    constructor() {
      super();

      this.input = this.querySelector("[data-discount-code-input]");
      this.applyButton = this.querySelector("[data-discount-code-apply]");
      this.removeButtons = this.querySelectorAll("[data-discount-code-remove]");

      this.applyButton?.addEventListener("click", this.handleApply.bind(this));
      this.input?.addEventListener("keydown", (event) => {
        if (event.key !== "Enter") {
          return;
        }

        event.preventDefault();
        this.handleApply();
      });

      this.removeButtons.forEach((button) => {
        button.addEventListener("click", this.handleRemove.bind(this));
      });
    }

    getAppliedCodesFromDom() {
      return [...this.querySelectorAll("[data-discount-applied-code]")]
        .map((el) => el.dataset.discountAppliedCode?.trim() ?? "")
        .filter(Boolean);
    }

    handleApply() {
      if (!this.input) {
        return;
      }

      const code = this.input.value.trim();

      if (!code) {
        return;
      }

      const existing = this.getAppliedCodesFromDom();
      const lower = code.toLowerCase();

      if (existing.some((c) => c.toLowerCase() === lower)) {
        return;
      }

      const payload = discountCodesToPayload([...existing, code]);
      this.updateDiscount(payload, {
        clearInputOnSuccess: true,
        attemptedCode: code,
      });
    }

    handleRemove(event) {
      const chip = event.currentTarget.closest("[data-discount-applied-code]");
      const codeToRemove = chip?.dataset.discountAppliedCode?.trim();

      if (!codeToRemove) {
        return;
      }

      const remaining = this.getAppliedCodesFromDom().filter(
        (c) => c.toLowerCase() !== codeToRemove.toLowerCase(),
      );

      this.updateDiscount(discountCodesToPayload(remaining), {
        clearInputOnSuccess: false,
        removedCode: codeToRemove,
      });
    }

    async updateDiscount(discount, methodOptions = {}) {
      const {
        clearInputOnSuccess = false,
        attemptedCode = "",
        removedCode = "",
      } = methodOptions;

      await WYATT.Cart.ready;

      const body = {
        discount,
        sections: WYATT.Cart.getSectionNames(),
        sections_url: window.location.pathname,
      };

      if (attemptedCode?.trim()) {
        body.attributes = {
          [DISCOUNT_VALIDATION_CODE_ATTRIBUTE]: attemptedCode.trim(),
        };
      }

      loading.add(this);

      const result = await WYATT.Cart.update(body, {
        messageOptions: {
          type: "error",
          minDelay: 6000,
          appendPoint:
            '[data-mini-cart][active="true"] [data-dialog-messages], #page-messages',
        },
      });

      loading.remove(this);

      if (!result?.sections) {
        return;
      }

      const discountCodeRows = getDiscountCodeRowsFromResponse(WYATT.Cart.data);
      const inapplicable =
        discountCodeRows === null
          ? []
          : getInapplicableRequestedCodes(discount, discountCodeRows);

      const attemptedTrimmed = attemptedCode?.trim() ?? "";
      const attemptedFailed =
        attemptedTrimmed.length > 0 &&
        inapplicable.some(
          (code) => code.toLowerCase() === attemptedTrimmed.toLowerCase(),
        );
      const shouldReportFailure = attemptedTrimmed
        ? attemptedFailed
        : inapplicable.length > 0;

      if (
        this.input &&
        clearInputOnSuccess &&
        discountCodeRows !== null &&
        !shouldReportFailure
      ) {
        this.input.value = "";
      }

      this.publish(
        WYATT.PubSub.events.updateDOM,
        WYATT.Cart.sectionUpdates(result.sections),
      );

      const cart = cartPayloadFromUpdateResponse(WYATT.Cart.data);
      const removedTrimmed = removedCode?.trim() ?? "";

      // The removal succeeded, so publish ahead of the inapplicable-code
      // check: re-sending the remaining codes can drop one that no longer
      // stacks, which does not unmake the removal.
      if (removedTrimmed) {
        WYATT.Analytics.publish(WYATT.Analytics.events.remove_promo_code, {
          cart,
          promo_code: removedTrimmed,
        });
      }

      if (!shouldReportFailure) {
        clearCartDiscountMessageHosts();

        if (attemptedTrimmed) {
          WYATT.Analytics.publish(WYATT.Analytics.events.submit_promo_code, {
            cart,
            promo_code: attemptedTrimmed,
            rejected: false,
            reason: null,
          });
        }

        return;
      }

      clearCartDiscountMessageHosts();

      const hasInlineHost =
        document.querySelectorAll("[data-cart-discount-messages]").length > 0;
      const appendPoint = hasInlineHost
        ? "[data-cart-discount-messages]"
        : "#page-messages";

      if (!attemptedTrimmed) {
        createMessage(getDiscountErrorMessage("invalid"), {
          type: "error",
          minDelay: 6000,
          appendPoint,
        });
        return;
      }

      const reason = await resolveDiscountFailureReason();

      WYATT.Analytics.publish(WYATT.Analytics.events.submit_promo_code, {
        cart,
        promo_code: attemptedTrimmed,
        rejected: true,
        reason,
      });

      const message = getDiscountErrorMessage(reason);

      createMessage(message, {
        type: "error",
        minDelay: 6000,
        appendPoint,
      });
    }
  },
);

export {
  buildDiscountValidationUrl,
  DISCOUNT_VALIDATION_CODE_ATTRIBUTE,
  getDiscountErrorMessage,
  getFailedDiscountCode,
  getInapplicableRequestedCodes,
  parseDiscountValidationResponse,
  resolveDiscountFailureReason,
};
